import { type ReactElement, type ReactNode, useEffect, useMemo, useRef } from 'react';
import type { Header, Row } from '@tanstack/react-table';

import { cn, Table, TableDensity, useContainerWidth } from '@crossnokaye/ux-common';

import { DataTableBody } from './table-body';
import { DataTableHead } from './table-head';
import { useTable } from '../context';
import { useTableDragScroll, useTableVirtualizer } from '../hooks';
import { calculateEnhancedSizes } from '../utils/calculate-enhanced-sizes';

// =============================================================================
// Props
// =============================================================================

interface TableRendererProps<TData> {
  onRowClick?: (row: Row<TData>) => void;
  bulkSelectBar?: ReactElement | null;
  columnOverlay?: ReactElement | null;
  cellHighlight?: ReactElement | null;
}

// Map our density type to ux-common's TableDensity enum
const DENSITY_MAP: Record<string, TableDensity> = {
  compact: TableDensity.COMPACT,
  comfortable: TableDensity.COMFORTABLE,
  default: TableDensity.DEFAULT,
  wide: TableDensity.WIDE,
};

/**
 * Computes CSS variables for column sizing with enhanced sizing to fill container.
 * Also syncs enhanced sizes back to state so TanStack knows about them during resize.
 * These variables are used to size columns: --col-{id}-size and --header-{id}-size
 */
const useEnhancedColumnSizeVars = (
  containerWidth: number,
  widthMode: 'auto' | 'full'
): Record<string, string> => {
  const {
    table,
    columnSizing,
    columnSizingInfo,
    columnConfigs,
    defaultColumnSize,
    updateColumnSizing,
  } = useTable();

  // Track if we've synced to avoid re-syncing during resize
  const hasSyncedRef = useRef(false);

  // Calculate enhanced sizes
  const effectiveSizes = useMemo(() => {
    if (widthMode !== 'full') return columnSizing;
    return calculateEnhancedSizes(columnConfigs, containerWidth, columnSizing, defaultColumnSize);
  }, [columnConfigs, containerWidth, columnSizing, widthMode, defaultColumnSize]);

  // Sync enhanced sizes to state (only when not actively resizing)
  useEffect(() => {
    // Don't sync during active resize - let TanStack manage the state
    if (columnSizingInfo.isResizingColumn) {
      hasSyncedRef.current = false;
      return;
    }

    // Only sync if we have calculated sizes and haven't synced yet
    if (widthMode === 'full' && containerWidth > 0 && !hasSyncedRef.current) {
      const newSizes = calculateEnhancedSizes(
        columnConfigs,
        containerWidth,
        columnSizing,
        defaultColumnSize
      );

      // Check if there are any new sizes to sync (grow column)
      const hasNewSizes = Object.keys(newSizes).some((id) => newSizes[id] !== columnSizing[id]);

      if (hasNewSizes) {
        updateColumnSizing(newSizes);
        hasSyncedRef.current = true;
      }
    }
  }, [
    columnSizingInfo.isResizingColumn,
    widthMode,
    containerWidth,
    columnConfigs,
    columnSizing,
    defaultColumnSize,
    updateColumnSizing,
  ]);

  // Generate CSS variables
  return useMemo(() => {
    const headers = table.getFlatHeaders();
    const vars: Record<string, string> = {};

    headers.forEach((header) => {
      const size = effectiveSizes[header.id] ?? header.getSize();
      vars[`--col-${header.id}-size`] = `${size}`;
      vars[`--header-${header.id}-size`] = `${size}`;
    });

    return vars;
  }, [table, effectiveSizes]);
};

/**
 * Main table renderer component.
 *
 * Renders the table structure with header and body.
 * Uses CSS Grid/Flex layout with CSS variables for column sizing.
 * Handles sticky header positioning and density-based row heights.
 */
export const TableRenderer = <TData,>({
  onRowClick,
  bulkSelectBar,
  columnOverlay,
  cellHighlight,
}: TableRendererProps<TData> = {}): ReactNode => {
  const {
    table,
    density,
    stickyHeader,
    tableId,
    widthMode,
    dragScroll,
    settings,
    tableRef,
    scrollContainerRef,
  } = useTable<TData>();

  // Drag scroll with click prevention - uses the shared scrollContainerRef from context
  const { isDragging, handlers, handleClickCapture } = useTableDragScroll({
    enabled: dragScroll,
    containerRef: scrollContainerRef,
  });

  // Measure container width for enhanced column sizing
  const containerWidth = useContainerWidth(scrollContainerRef);

  const headerGroups = table.getHeaderGroups();
  const headerCells = (headerGroups[0]?.headers || []) as Header<TData, unknown>[];
  const rows = table.getRowModel().rows;

  // Virtualization
  const { virtualItems, totalSize, isVirtualized } = useTableVirtualizer({
    containerRef: scrollContainerRef,
    rows,
    enabled: settings?.virtualize?.enabled ?? false,
    density,
    overscan: settings?.virtualize?.overscan,
    estimateSize: settings?.virtualize?.estimateSize,
  });

  const columnSizeVars = useEnhancedColumnSizeVars(containerWidth, widthMode);
  const tableDensity = DENSITY_MAP[density] || TableDensity.DEFAULT;

  return (
    <>
      {/* Render BulkSelectBar above the table (it has its own visibility logic) */}
      {bulkSelectBar}

      <div
        ref={scrollContainerRef}
        className={cn(
          'data-table2-container relative h-full w-full',
          // Only apply overflow-auto if:
          // 1. Sticky header is disabled (allow internal scroll), OR
          // 2. Sticky header is enabled WITH explicit height (internal scroll with sticky)
          // When sticky is enabled without height, assume external scroll container
          !stickyHeader.enabled || stickyHeader.height ? 'overflow-auto' : 'overflow-visible',
          stickyHeader.enabled &&
            stickyHeader.height &&
            'max-h-[calc(100vh-var(--sticky-offset,0))]',
          dragScroll && 'select-none',
          isDragging && 'cursor-grabbing'
        )}
        style={
          stickyHeader.height
            ? ({
                '--sticky-offset': stickyHeader.height,
              } as React.CSSProperties)
            : undefined
        }
        data-component="data-table-scroll-container"
        data-dragging={isDragging || undefined}
        {...handlers}
        onClickCapture={dragScroll ? handleClickCapture : undefined}
      >
        <Table
          ref={tableRef}
          className={cn(
            'border-separate border-spacing-0 grid',
            widthMode === 'full' ? 'w-full' : 'w-fit'
          )}
          density={tableDensity}
          style={{
            ...columnSizeVars,
            isolation: 'isolate',
          }}
          data-component="data-table"
          data-testid={tableId}
          role="table"
        >
          <colgroup>
            {headerCells
              .filter((header): boolean => header.column.getIsVisible())
              .map(
                (header, index): ReactElement => (
                  <col
                    key={`col${index}`}
                    style={{
                      width: `calc(var(--header-${header?.id}-size) * 1px)`,
                    }}
                  />
                )
              )}
          </colgroup>

          <DataTableHead headerGroups={headerGroups} stickyHeader={stickyHeader.enabled} />
          <DataTableBody
            rows={rows}
            virtualItems={virtualItems}
            totalSize={totalSize}
            isVirtualized={isVirtualized}
            onRowClicked={onRowClick}
          />
          {columnOverlay}
          {cellHighlight}
        </Table>
      </div>
    </>
  );
};

TableRenderer.displayName = 'DataTable.TableRenderer';
